import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useDebouncedCallback } from "use-debounce";
import { Link, useHistory } from "react-router-dom";
import { useFormikContext } from "formik";
import querystring from "querystring";
import { Form, CardTitle, Field, useFeatures, Button } from "@cordata/buffet";
import {
  Card,
  CardBody,
  CardFooter,
  Row,
  Col,
  UncontrolledButtonDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Table,
  UncontrolledTooltip,
  Alert
} from "reactstrap";
import { uniqBy, orderBy, compact, pickBy, flatten } from "lodash";
import * as Yup from "yup";
import { adminInstance as Admin, toastSuccess, toastError } from "~/utils";
import CordataModal from "~/components/common/Modal";
import { BaseButton } from "~/components/common";

import { sendActivationEmail } from "~/pages/admin/users/collection";

const mfaType = ["sms", "email", "totp"] as const;

const labels: Record<typeof mfaType[number], string> = {
  sms: "SMS/Text Message",
  email: "Email",
  totp: "Authenticator app (Google Authenticator, Authy, etc.)"
};

const mfaTypeOptions = (
  currentMfaType: "sms" | "email" | "totp" | null
) =>
  mfaType.map(v => ({
    label: labels[v],
    value: v,
    disabled:
      (v === "totp" && currentMfaType !== "totp")
  }));

const authenticationServices = ["cordata", "microsoft"];

const activationMethods = ["SMS", "Email"];

const activationMethodOptions = activationMethods.map(v => ({
  label: v,
  value: v
}));

const KNOWN_DUMMY_PHONES = ["0123456789", "1234567890"];

const isValidMobileNumber = (phone: string | null | undefined): boolean => {
  if (!phone) return false;
  const digits = phone.replace(/[^0-9]/g, "");
  if (digits.length < 10) return false;
  if (/[a-zA-Z]/.test(phone)) return false;
  if (/[^0-9()+\-\s]/.test(phone)) return false;
  if (new Set(digits).size === 1) return false;
  if (KNOWN_DUMMY_PHONES.includes(digits)) return false;
  return true;
};

const DEFAULT_VALUES = {
  appUser: {
    authenticationService: null,
    accountIsLocked: false,
    mfaRequired: true,
    mfaType: "email",
    firstName: "",
    lastName: "",
    userName: "",
    emailAddress: "",
    mobilePhone: "",
    isClientManaged: true,
    activationMethod: "SMS",
    secondaryEmailAddress: ""
  },
  securityGroups: [],
  orgSecurityGroups: [],
  provider: {
    providerSpecialtyUid: "",
    providerUid: "",
    specialty: null
  },
  divisions: []
};

interface Division {
  id: string;
  divisionUid: string;
  divisionName: string;
}

interface AppUser {
  id: string;
  firstName: string;
  lastName: string;
  displayName: string;
  userName: string;
  emailAddress: string;
  mobilePhone: string;
  mfaRequired: boolean;
  mfaType: string | null;
  authenticationService: string;
  isClientManaged: boolean;
  smsOptedIn?: boolean;
  activationMethod: string | null;
  secondaryEmailAddress: string | null;
}

interface Provider {
  id: string;
  providerUid: string;
  providerSpecialtyUid: string;
  providerDisplayName: string;
}

interface AppUserSecurityGroup {
  id: string;
  securityGroupUid: string;
  divisionUid: string;
  orgUid: string;
  label: string;
  name: string;
  refDataType: string;
}

interface Specialty {
  id: string;
  label: string;
  providerSpecialtyUid: string;
}

interface AppUserDepartment {
  departmentUid: string;
  divisionUid: string;
  orgUid: string;
  departmentName: string;
  departmentLabel: string;
}

interface User {
  appUser: AppUser;
  provider: Provider;
  appUserSecurityGroup: AppUserSecurityGroup[];
  associateUser: Provider[];
  appUserDepartment: AppUserDepartment[];
}

const DisplayNameField = () => {
  const {
    values: {
      appUser: { firstName, lastName, displayName }
    },
    touched,
    setFieldValue,
    setFieldTouched
  }: any = useFormikContext();

  const defaultDisplayName = compact([firstName, lastName]).join(" ");
  const userTouchedDisplayName =
    (touched.appUser?.displayName || false) && defaultDisplayName !== "";

  useEffect(() => {
    // auto-set displayName from firstName + lastName unless user manually sets displayName
    if (!userTouchedDisplayName) {
      setFieldValue("appUser.displayName", defaultDisplayName);
      setFieldTouched("appUser.displayName", false);
    }
  }, [firstName, lastName]);

  useEffect(() => {
    // if user clears displayName or tabs through displayName after it's been
    // auto-set, allow auto-setting displayName again
    // tabbing through the displayName field sets touched to true, so we need to guard against that
    if (displayName === "" || displayName === defaultDisplayName) {
      setFieldTouched("appUser.displayName", false);
    }
  }, [displayName, userTouchedDisplayName]);

  useEffect(() => {
    // if user clears displayName, auto-set displayName after they tab away
    // tabbing away from the field sets touched to true, so need to auto-set displayName after that
    if (userTouchedDisplayName && displayName === "") {
      setFieldValue("appUser.displayName", defaultDisplayName);
      setFieldTouched("appUser.displayName", false);
    }
  }, [userTouchedDisplayName]);

  return <Field name="appUser.displayName" label="Display Name" required />;
};

const UserNameField = ({
  onStatusChange
}: {
  onStatusChange: React.Dispatch<React.SetStateAction<"available" | "taken">>;
}) => {
  const { values }: { values: any } = useFormikContext();

  const checkAvailability = useDebouncedCallback(v => {
    Admin.get("app-user-2/available", {
      params: {
        user_name: v,
        ...(values.appUser.id && { "user-uid": values.appUser.id })
      }
    }).then(r => {
      if (r.data.result) {
        onStatusChange("available");
      } else {
        onStatusChange("taken");
      }
    });
  }, 200);

  useEffect(() => {
    if (values.appUser.userName) {
      checkAvailability(values.appUser.userName);
    }
  }, [values.appUser.userName]);

  return (
    <Field
      name="appUser.userName"
      label="User Name"
      required
      validateOnChange
    />
  );
};

const EmailAddressField = ({
  onStatusChange
}: {
  onStatusChange: React.Dispatch<React.SetStateAction<"available" | "taken">>;
}) => {
  const { values }: { values: any } = useFormikContext();

  const checkAvailability = useDebouncedCallback(v => {
    Admin.get("app-user-2/available", {
      params: {
        email_address: v,
        ...(values.appUser.id && { "user-uid": values.appUser.id })
      }
    }).then(r => {
      if (r.data.result) {
        onStatusChange("available");
      } else {
        onStatusChange("taken");
      }
    });
  }, 200);

  useEffect(() => {
    if (
      values.appUser.emailAddress &&
      Yup.string()
        .email()
        .isValidSync(values.appUser.emailAddress)
    ) {
      checkAvailability(values.appUser.emailAddress);
    }
  }, [values.appUser.emailAddress]);

  return (
    <Field
      name="appUser.emailAddress"
      label="Email Address"
      required
      validateOnChange
    />
  );
};

const MobilePhoneField = ({
  onStatusChange,
  smsOptedIn,
  isPhoneReal
}: {
  onStatusChange: (
    v: boolean | null,
    canOptIn?: boolean,
    isPhoneReal?: boolean,
    twilioFromPhone?: string | null
  ) => void;
  smsOptedIn: boolean | null;
  isPhoneReal: boolean;
}) => {
  const { values, setFieldValue }: { values: any; setFieldValue: any } = useFormikContext();

  const checkOptIn = useDebouncedCallback((v: string) => {
    Admin.get("app-user-2/phone-opted-in", {
      params: { mobile_phone: v }
    })
      .then(r =>
        onStatusChange(
          r.data.result,
          r.data.canOptIn,
          r.data.isPhoneReal,
          r.data.twilioFromPhone
        )
      )
      .catch(() => { });
  }, 200);

  useEffect(() => {
    if (values.appUser.mobilePhone) {
      checkOptIn(values.appUser.mobilePhone);
    } else {
      onStatusChange(null);
    }
  }, [values.appUser.mobilePhone]);

  useEffect(() => {
    if (!isValidMobileNumber(values.appUser.mobilePhone)) {
      const mfaType = values.appUser.mfaType?.value || values.appUser.mfaType;
      if (mfaType === "sms") {
        setFieldValue("appUser.mfaType", "email");
      }
      const activationMethod =
        values.appUser.activationMethod?.value || values.appUser.activationMethod;
      if (activationMethod === "SMS") {
        setFieldValue("appUser.activationMethod", "Email");
      }
    }
  }, [values.appUser.mobilePhone]);

  const helpText =
    isPhoneReal === false
      ? "This does not appear to be a valid phone number"
      : smsOptedIn === null
        ? undefined
        : smsOptedIn
          ? "This phone number is opted in to SMS"
          : "This phone number is not opted in to SMS";

  return (
    <Field
      name="appUser.mobilePhone"
      label="Mobile Number"
      type="tel"
      validateOnChange
      text={helpText}
    />
  );
};

const AuthenticationServiceField = ({ divisions }) => {
  const {
    values: {
      appUser: { authenticationService }
    },
    setFieldValue,
    setFieldTouched
  }: any = useFormikContext();

  useEffect(() => {
    Admin.get("division-setting", {
      params: {
        "division-uid": divisions.map(d => d.divisionUid),
        "setting-name": "default_authentication_service"
      },
      paramsSerializer: p => querystring.stringify(p)
    }).then(r => {
      const authServices = Array.from(
        new Set(
          r.data.data.map(svc => svc.attributes.settingValue.toLowerCase())
        )
      );
      if (authServices.length == 1 && !authenticationService) {
        setFieldValue("appUser.authenticationService", authServices[0]);
        setFieldTouched("appUser.authenticationService", false);
      }
    });
  }, [divisions]);

  return (
    <Field
      name="appUser.authenticationService"
      label="Authentication Service"
      type="select"
      options={authenticationServices}
      required
    />
  );
};

interface UserFormProps {
  user?: User;
  meta?: any;
  canEdit?: boolean;
}

const UserForm = ({ user, meta, canEdit = false }: UserFormProps) => {
  const { hasAppKickstarter } = useFeatures();
  const history = useHistory();

  const [divisions, setDivisions] = useState<Division[]>([]);
  const [orgSecurityGroups, setOrgSecurityGroups] = useState<
    AppUserSecurityGroup[]
  >([]);
  const [divisionSecurityGroups, setDivisionSecurityGroups] = useState<
    AppUserSecurityGroup[]
  >([]);
  const [specialties, setSpecialties] = useState<Specialty[]>([]);
  const [associateUsers, setAssociateUsers] = useState<Provider[]>([]);

  const [options, setOptions] = useState<any>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [userNameAvailability, setUserNameAvailability] = useState<
    "available" | "taken"
  >("available");
  const [emailAvailability, setEmailAvailability] = useState<
    "available" | "taken"
  >("available");
  const [phoneOptedIn, setPhoneOptedIn] = useState<boolean | null>(
    user && user.appUser && user.appUser.smsOptedIn !== undefined
      ? user.appUser.smsOptedIn
      : null
  );
  const [phoneCanOptIn, setPhoneCanOptIn] = useState<boolean>(true);
  const [isPhoneReal, setIsPhoneReal] = useState<boolean>(true);
  const [twilioFromPhone, setTwilioFromPhone] = useState<string | null>(null);
  const [smsMessages, setSmsMessages] = useState<any[]>([]);
  const [smsMessagesLoading, setSmsMessagesLoading] = useState(false);
  const [smsUserPhone, setSmsUserPhone] = useState<string | null>(null);
  const [smsModalOpen, setSmsModalOpen] = useState(false);

  const fetchSmsLog = () => {
    if (!user?.appUser?.id || !hasAppKickstarter) return;
    setSmsMessagesLoading(true);
    Admin.get("app-user-2/sms-message-log", {
      params: { "user-uid": user.appUser.id }
    })
      .then(r => {
        setSmsMessages(r.data.data || []);
        setSmsUserPhone(r.data.userPhone || null);
        setSmsModalOpen(true);
      })
      .catch(() => setSmsMessages([]))
      .finally(() => setSmsMessagesLoading(false));
  };

  const handlePhoneOptedInChange = (
    v: boolean | null,
    canOptIn?: boolean,
    isPhoneReal?: boolean,
    twilioPhone?: string | null
  ) => {
    setPhoneOptedIn(v);
    if (canOptIn !== undefined) setPhoneCanOptIn(canOptIn);
    if (isPhoneReal !== undefined) setIsPhoneReal(isPhoneReal);
    if (twilioPhone !== undefined) setTwilioFromPhone(twilioPhone || null);
  };

  useEffect(() => {
    if (meta) {
      setOptions(meta.options);
    } else {
      Admin.get("app-user-2/__schema__", {
        params: {
          "org-uid": localStorage.getItem("org")
        }
      }).then(r => setOptions(r.data.options));
    }
  }, [meta]);

  useEffect(() => {
    if (options) {
      const ausg = options.appUserSecurityGroup;

      setDivisions(
        orderBy(
          uniqBy(
            ausg.filter(x => x.divisionUid),
            "divisionUid"
          ).map(d => ({
            divisionUid: d.divisionUid,
            divisionName: d.divisionName
          })),
          "divisionName"
        )
      );

      const orgGroups = ausg.filter(sg => sg.refDataType === "organization");
      const divisionGroups = ausg.filter(sg => sg.refDataType === "division");

      if (hasAppKickstarter) {
        setOrgSecurityGroups(orgGroups);
        setDivisionSecurityGroups(divisionGroups);
      } else {
        setOrgSecurityGroups(
          orgGroups.filter(sg => !sg.name.toLowerCase().includes("researcher"))
        );
        setDivisionSecurityGroups(
          divisionGroups.filter(
            sg => !sg.name.toLowerCase().includes("researcher")
          )
        );
      }

      setSpecialties(options.provider.providerSpecialtyUid);

      setAssociateUsers(options.associateUser);

      setIsLoading(false);
    }
  }, [options, hasAppKickstarter]);

  const optInSms = () => {
    setPhoneOptedIn(true);
  };

  const [shouldSendActivation, setShouldSendActivation] = useState(false);

  const orgCode = localStorage.getItem("org")?.slice(-6);

  const saveUser = (values, formikBag) => {
    formikBag.setSubmitting(true);

    const payload = {
      smsOptIn: phoneOptedIn,
      appUser: {
        ...values.appUser,
        authenticationService:
          values.appUser.authenticationService?.value ||
          values.appUser.authenticationService,
        mfaRequired: hasAppKickstarter
          ? !!values.appUser.mfaRequired
          : values.appUser.id
            ? !!values.appUser.mfaRequired
            : true,
        mfaType:
          values.appUser.mfaType != null
            ? values.appUser.mfaType.value
            : values.appUser.mfaRequired
              ? "email"
              : null, // default mfaType is email
        activationMethod:
          values.appUser.activationMethod?.value ||
          values.appUser.activationMethod
      },
      appUserSecurityGroup: [
        ...values.orgSecurityGroups,
        ...values.securityGroups.filter(g =>
          values.divisions.some(d => d.divisionUid === g.divisionUid)
        )
      ],
      associateUser: values.securityGroups?.some(sg =>
        divisionSecurityGroups.some(
          dsg =>
            dsg.securityGroupUid === sg.securityGroupUid &&
            dsg.name === "Associate User"
        )
      )
        ? values.associateUser || []
        : [],
      orgUid: localStorage.getItem("org"),
      ...(values.provider &&
        values.provider.specialty && {
        provider: {
          providerSpecialtyUid: values.provider.specialty.id
        }
      }),
      appUserDepartment: compact(
        flatten(
          Object.values(
            pickBy(values, (v, k) => k.startsWith("appUserDepartment-"))
          )
        )
      ).filter(aud =>
        values.divisions.some(d => d.divisionUid === aud.divisionUid)
      )
    };

    const method = values.appUser.id ? "put" : "post";
    const url = `/app-user-2${values.appUser.id ? `/${values.appUser.id}` : ""
      }`;

    delete payload.appUser.userUid;
    delete payload.appUser.id;

    Admin[method](url, payload)
      .then(() => {
        if (shouldSendActivation) {
          sendActivationEmail(
            values.appUser.userName,
            orgCode,
            values.appUser.mobilePhone
          ).then(() => {
            history.push("/admin/users");
          });
        } else {
          toastSuccess();
          history.push("/admin/users");
        }
      })
      .catch(e => {
        toastError("Error occurred while submitting user");
        console.error(e);
      })
      .finally(() => {
        formikBag.setSubmitting(false);
      });
  };

  const validationSchema = Yup.object().shape({
    appUser: Yup.object().shape({
      firstName: Yup.string().required(),
      lastName: Yup.string().required(),
      displayName: Yup.string().required(),
      userName: Yup.string()
        .required()
        .test(
          "availability",
          "This user name is already in use",
          () => userNameAvailability === "available"
        ),
      emailAddress: Yup.string()
        .required()
        .email("Must be a valid email address")
        .test(
          "availability",
          "This email address is already in use",
          () => emailAvailability === "available"
        ),
      secondaryEmailAddress: Yup.string()
        .nullable()
        .email("Must be a valid email address")
        .test(
          "not-primary-email",
          "Secondary email must differ from primary email",
          function (this: any, value) {
            return !value || value.toLowerCase() !== this.parent.emailAddress?.toLowerCase();
          }
        )
        .test(
          "secondaryEmail-required",
          "secondaryEmailAddress is a required field",
          function (this: any, value) {
            const authService = this.parent.authenticationService?.value || this.parent.authenticationService;
            if (authService !== "cordata") return true;
            const method = this.parent.activationMethod?.value || this.parent.activationMethod;
            if (method === "Email") return !!value;
            return true;
          }
        ),
      activationMethod: Yup.mixed()
        .when("authenticationService", {
          is: value => {
            const authService = value?.value || value;
            return !authService || authService === "cordata";
          },
          then: schema => schema.required("Activation method is required")
        })
        .test(
          "sms-requires-phone",
          "SMS activation method requires a valid phone number",
          function (this: any, value) {
            const authService =
              this.parent.authenticationService?.value ||
              this.parent.authenticationService;
            if (authService && authService !== "cordata") return true;
            const method = value?.value || value;
            if (method === "SMS") return isValidMobileNumber(this.parent.mobilePhone);
            return true;
          }
        ),
      mobilePhone: Yup.string()
        .nullable()
        .test(
          "mobilePhone-telephone",
          "must be a valid phone number",
          function (this: any, value) {
            if (!value) return true;
            return /^((\+[1-9]{1,4}[ -]*)|(\([0-9]{2,3}\)[ -]*)|([0-9]{2,4})[ -]*)*?[0-9]{3,4}?[ -]*[0-9]{3,4}?$/.test(value);
          }
        ),
      authenticationService: Yup.object()
        .nullable()
        .required(),
      mfaRequired: Yup.boolean().nullable(),
      mfaType: Yup.mixed()
        .nullable()
        .when("mfaRequired", {
          is: true,
          then: schema =>
            schema.required("2FA method is required when MFA is enabled")
        })
        .test(
          "sms-valid-phone",
          "SMS 2FA requires a valid mobile number",
          function (this: any, value) {
            const mfaTypeValue =
              value && value.value !== undefined ? value.value : value;
            if (mfaTypeValue !== "sms") return true;
            return isValidMobileNumber(this.parent.mobilePhone);
          }
        )
    }),
    securityGroups: Yup.array().required(),
    orgSecurityGroups: Yup.array().nullable(),
    appUserDepartment: Yup.array().nullable(),
    associateUser: Yup.array().when(
      "securityGroups",
      (securityGroups, schema) => {
        if (
          securityGroups.some(
            sg =>
              sg.securityGroupUid ===
              divisionSecurityGroups.find(dsg => dsg.name === "Associate User")
                ?.securityGroupUid
          )
        ) {
          return schema
            .of(
              Yup.object().shape({
                providerUid: Yup.string().required()
              })
            )
            .nullable()
            .required("Need to select at least one associate user");
        }

        return schema.nullable();
      }
    ),
    divisions: Yup.array().required(),
    ...(!user && {
      provider: Yup.object()
        .shape({
          specialty: Yup.object()
            .shape()
            .nullable()
            .required()
        })
        .required()
    })
  });

  if (isLoading) return null;

  return (
    <Form
      displayName="UserForm"
      validateOnBlur
      enableReinitialize
      initialValues={{
        orgCode,
        ...DEFAULT_VALUES,
        ...(user && {
          ...user,
          appUser: {
            ...user.appUser,
            mfaType:
              user.appUser.mfaType != null
                ? user.appUser.mfaType
                : user.appUser.mfaRequired
                  ? "email"
                  : null,
            activationMethod: user.appUser.activationMethod || "SMS",
            secondaryEmailAddress: user.appUser.secondaryEmailAddress || ""
          },
          divisions: divisions.filter(d =>
            user.appUserSecurityGroup.some(
              sg => sg.divisionUid === d.divisionUid
            )
          ),
          securityGroups: user.appUserSecurityGroup.filter(
            sg => sg.divisionUid
          ),
          orgSecurityGroups: user.appUserSecurityGroup.filter(
            sg => !sg.divisionUid
          ),
          provider: {
            specialty: user.provider.providerSpecialtyUid
          },
          ...user.appUserDepartment?.reduce((acc, aud) => {
            const fieldKey = `appUserDepartment-${aud.divisionUid}`;
            acc[fieldKey] = acc[fieldKey] || [];
            acc[fieldKey].push(aud);
            return acc;
          }, {})
        })
      }}
      initialTouched={{
        appUser: {
          displayName: user
            ? user.appUser.displayName !==
            compact([user.appUser.firstName, user.appUser.lastName]).join(" ")
            : false
        }
      }}
      onSubmit={saveUser}
      validationSchema={validationSchema}
      noValidate
    >
      {({ values, isSubmitting, dirty, handleSubmit }) => {
        const isCordataAuth = () => {
          const authService = values.appUser.authenticationService?.value || values.appUser.authenticationService;
          return !authService || authService === "cordata";
        };

        return (
          <Card>
            <CardBody className="p-0">
              <fieldset disabled={!canEdit}>
                <CardTitle>Profile</CardTitle>

                <Row form>
                  <Col sm>
                    <Field name="appUser.firstName" label="First Name" required />
                  </Col>

                  <Col sm>
                    <Field name="appUser.lastName" label="Last Name" required />
                  </Col>

                  <Col sm>
                    <DisplayNameField />
                  </Col>

                  <Col sm>
                    <UserNameField onStatusChange={setUserNameAvailability} />
                  </Col>
                </Row>

                <Row form>
                  <Col sm>
                    <EmailAddressField onStatusChange={setEmailAvailability} />
                  </Col>

                  <Col sm>
                    <MobilePhoneField
                      onStatusChange={handlePhoneOptedInChange}
                      smsOptedIn={phoneOptedIn}
                      isPhoneReal={isPhoneReal}
                    />
                    {canEdit &&
                      hasAppKickstarter &&
                      values.appUser.mobilePhone &&
                      isPhoneReal &&
                      phoneOptedIn === false &&
                      (phoneCanOptIn ? (
                        <BaseButton
                          color="success"
                          outline
                          size="sm"
                          icon="check"
                          text="Opt in to SMS"
                          onClick={() => optInSms()}
                        />
                      ) : (
                        <small className="text-muted">
                          Unsubscribed via SMS. To re-enable, text{" "}
                          <strong>START</strong> to{" "}
                          {twilioFromPhone ? (
                            <strong>{twilioFromPhone}</strong>
                          ) : (
                            "our SMS number"
                          )}
                          .
                        </small>
                      ))}
                    {user &&
                      hasAppKickstarter &&
                      values.appUser.mobilePhone &&
                      values.appUser.mobilePhone === user.appUser.mobilePhone && (
                        <div className="mt-2">
                          <Button
                            color="secondary"
                            outline
                            size="sm"
                            disabled={smsMessagesLoading}
                            onClick={fetchSmsLog}
                          >
                            {smsMessagesLoading
                              ? "Loading..."
                              : "View SMS Message Log"}
                          </Button>
                        </div>
                      )}
                  </Col>
                  <Col
                    sm
                    hidden={values.appUser.id} // hide on user edit
                  >
                    <Field
                      name="provider.specialty"
                      options={specialties}
                      type="select"
                      getOptionValue={o => o.id}
                      getOptionLabel={o => o.label}
                      required
                    />
                  </Col>
                </Row>
              </fieldset>
              <fieldset>
                <CardTitle>Authentication</CardTitle>
                <Row form>
                  <Col sm>
                    <AuthenticationServiceField divisions={divisions} />
                  </Col>
                </Row>
                {hasAppKickstarter && isCordataAuth() && (
                  <>
                    <Row form>
                      <Col sm>
                        <Field
                          name="appUser.mfaRequired"
                          label="Require two-factor authentication"
                          type="checkbox"
                          placeholder="Yes"
                          disabled={
                            values.canImpersonate ||
                            (values.authenticationService &&
                              values.authenticationService.value !== "cordata")
                          }
                        />
                      </Col>
                      <Col sm>
                        <Field
                          name="appUser.mfaType"
                          label="2FA Method"
                          type="select"
                          options={mfaTypeOptions(
                            values.appUser.mfaType || null,
                          )}
                          isOptionDisabled={option =>
                            (option.value === "totp" &&
                              values.appUser.mfaType !== "totp") ||
                            (option.value === "sms" &&
                              !isValidMobileNumber(values.appUser.mobilePhone))
                          }
                        />
                      </Col>
                    </Row>
                    <Row form>
                      <Col sm>
                        <Field
                          name="appUser.isClientManaged"
                          label="Client Managed"
                          type="checkbox"
                          placeholder="Yes"
                        />
                      </Col>
                    </Row>
                  </>
                )}
              </fieldset>
              {isCordataAuth() && (
                <fieldset disabled={!canEdit}>
                  <CardTitle>Activation/Reset</CardTitle>
                  <Alert color="info">
                    <FontAwesomeIcon icon="info-circle" className="mr-1" />
                    This section is used only for account activation or password
                    reset
                  </Alert>
                  <Row>
                    <Col sm>
                      <Field
                        name="appUser.activationMethod"
                        label="Activation Method"
                        type="select"
                        required
                        options={activationMethodOptions}
                        isOptionDisabled={option =>
                          option.value === "SMS" && (!isPhoneReal || !isValidMobileNumber(values.appUser.mobilePhone))
                        }
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col sm>
                      <Field
                        name="appUser.secondaryEmailAddress"
                        label="Secondary Email Address"
                        required={
                          values.appUser.activationMethod?.value === "Email"
                        }
                      />
                    </Col>
                  </Row>
                </fieldset>)}

              <fieldset disabled={!canEdit}>
                <CardTitle>Divisions & Permissions</CardTitle>

                {orgSecurityGroups.length > 0 && (
                  <Row form>
                    <Col sm>
                      <Field
                        name="orgSecurityGroups"
                        batch={false}
                        type="checkbox-group"
                        label="Organization Security Groups"
                        options={orgSecurityGroups}
                        getOptionLabel={o => o.label}
                        getOptionValue={o =>
                          `${o.divisionUid}-${o.securityGroupUid}`
                        }
                      />
                    </Col>
                  </Row>
                )}

                <Row form>
                  <Col sm>
                    <Field
                      name="divisions"
                      type="checkbox-group"
                      batch={false}
                      label="Divisions"
                      required
                      options={divisions}
                      getOptionLabel={d => d.divisionName}
                      getOptionValue={d => d.divisionUid}
                      components={{
                        SubOption: ({ option: division }: any) => (
                          <>
                            {values.divisions?.includes(division) && (
                              <div className="ml-3">
                                <Field
                                  name="securityGroups"
                                  required
                                  columns={
                                    divisionSecurityGroups.filter(
                                      sg =>
                                        sg.divisionUid === division.divisionUid
                                    ).length %
                                      3 ===
                                      0
                                      ? 3
                                      : 2
                                  }
                                  label={false}
                                  batch={false}
                                  type="checkbox-group"
                                  options={divisionSecurityGroups.filter(
                                    sg => sg.divisionUid === division.divisionUid
                                  )}
                                  getOptionLabel={o => o.label}
                                  getOptionValue={o =>
                                    `${o.divisionUid}-${o.securityGroupUid}`
                                  }
                                />

                                {options?.appUserDepartment?.filter(
                                  aud => aud.divisionUid === division.divisionUid
                                ).length > 0 && (
                                    <Field
                                      label="Departments"
                                      name={`appUserDepartment-${division.divisionUid}`}
                                      options={options?.appUserDepartment
                                        .filter(
                                          aud =>
                                            aud.divisionUid === division.divisionUid
                                        )
                                        .sort((a, b) =>
                                          a.departmentLabel
                                            .toLowerCase()
                                            .localeCompare(
                                              b.departmentLabel.toLowerCase()
                                            )
                                        )}
                                      type="select"
                                      multi
                                      getOptionLabel={o => o.departmentLabel}
                                      getOptionValue={o => o.departmentUid}
                                    />
                                  )}
                              </div>
                            )}
                          </>
                        )
                      }}
                    />
                  </Col>
                </Row>

                {values.securityGroups?.some(sg =>
                  divisionSecurityGroups.some(
                    dsg =>
                      dsg.securityGroupUid === sg.securityGroupUid &&
                      dsg.name === "Associate User"
                  )
                ) && (
                    <Row form>
                      <Col sm>
                        <Field
                          name="associateUser"
                          label="Associate Users"
                          type="select"
                          getOptionValue={o => o.providerUid}
                          getOptionLabel={o => o.providerDisplayName}
                          options={associateUsers}
                          multi
                          required
                        />
                      </Col>
                    </Row>
                  )}
              </fieldset>

              <fieldset disabled={!canEdit}>
                {values.appUser.id && hasAppKickstarter && (
                  <Field
                    name="appUser.id"
                    text="Unique identifier for internal use"
                    disabled
                  />
                )}
              </fieldset>

              {user && hasAppKickstarter && (
                <CordataModal
                  isOpen={smsModalOpen}
                  title="SMS Message Log"
                  size="lg"
                  onClose={() => setSmsModalOpen(false)}
                  showCloseButton
                  action={
                    <Button
                      color="secondary"
                      onClick={() => setSmsModalOpen(false)}
                    >
                      Close
                    </Button>
                  }
                >
                  {smsMessages.length === 0 ? (
                    <div className="text-muted">No messages recorded.</div>
                  ) : (
                    <Table hover size="sm" className="mb-0">
                      <thead>
                        <tr>
                          <th
                            id="sms-dir-header"
                            style={{ cursor: "default", whiteSpace: "nowrap" }}
                          >
                            Dir{" "}
                            <FontAwesomeIcon
                              icon="info-circle"
                              size="sm"
                              className="text-muted"
                            />
                            <UncontrolledTooltip target="sms-dir-header">
                              IN = message received from user. OUT = message sent
                              to user.
                            </UncontrolledTooltip>
                          </th>
                          <th>Date</th>
                          <th>Service</th>
                          <th>From</th>
                          <th>To</th>
                          <th>Keyword</th>
                          <th>Message</th>
                        </tr>
                      </thead>
                      <tbody>
                        {smsMessages.map(row => {
                          const userPhone = (smsUserPhone || "")
                            .replace(/\D/g, "")
                            .slice(-10);
                          const fromNorm = (row.fromPhone || "")
                            .replace(/\D/g, "")
                            .slice(-10);
                          const direction =
                            userPhone && fromNorm === userPhone ? "IN" : "OUT";
                          const date = row.createdDate
                            ? new Date(row.createdDate)
                            : null;
                          return (
                            <tr key={row.smsMessageUid}>
                              <td>
                                <span
                                  className={`badge badge-${direction === "IN" ? "success" : "primary"
                                    }`}
                                  style={{
                                    display: "inline-block",
                                    width: "2.5rem",
                                    textAlign: "center"
                                  }}
                                >
                                  {direction}
                                </span>
                              </td>
                              <td>
                                {date && !isNaN(date.getTime())
                                  ? date.toLocaleString()
                                  : row.createdDate}
                              </td>
                              <td>{row.serviceName || "-"}</td>
                              <td>{row.fromPhone || "-"}</td>
                              <td>{row.toPhone || "-"}</td>
                              <td>{row.keyword || "-"}</td>
                              <td>{row.message}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  )}
                </CordataModal>
              )}
            </CardBody>

            <CardFooter className="d-flex">
              <div className="ml-auto">
                <Button
                  outline
                  color="tertiary"
                  type="cancel"
                  tag={Link}
                  to="/admin/users"
                >
                  Cancel
                </Button>

                <UncontrolledButtonDropdown direction="down">
                  <BaseButton
                    disabled={isSubmitting || !dirty || !canEdit}
                    color="primary"
                    icon="save"
                    onClick={() => {
                      setShouldSendActivation(false);
                      handleSubmit();
                    }}
                    text={isSubmitting ? "Working..." : "Save"}
                  />

                  {!values.appUser.id &&
                    (!values.appUser.authenticationService ||
                      values.appUser.authenticationService?.value ===
                      "cordata") && (
                      <>
                        <DropdownToggle caret color="primary" />

                        <DropdownMenu bottom>
                          <DropdownItem
                            onClick={() => {
                              setShouldSendActivation(true);
                              handleSubmit();
                            }}
                          >
                            Save &amp; Send Activation
                          </DropdownItem>
                        </DropdownMenu>
                      </>
                    )}
                </UncontrolledButtonDropdown>
              </div>
            </CardFooter>
          </Card>
        )
      }}
    </Form>
  );
};

export default UserForm;
